import { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { AddUserForm } from "@/components/users/AddUserForm";
import { UserRow } from "@/components/users/UserRow";

const NoAccess = () => (
  <div data-testid="users-no-access" className="mx-auto mt-10 flex max-w-md flex-col items-center gap-2 rounded-2xl border border-slate-800 bg-slate-900 p-8 text-center text-slate-300">
    <ShieldAlert className="h-6 w-6 text-slate-400" />
    <p className="font-semibold">You don't have access to this page</p>
    <p className="text-sm text-slate-400">Only admins can manage users.</p>
  </div>
);

const upsert = (list, item) => (list.some((x) => x.id === item.id) ? list.map((x) => (x.id === item.id ? item : x)) : [...list, item]);

function UsersAdmin() {
  const { user, setUser } = useAuth();
  const { reps, repsById } = useData();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => setError(errMsg(e, "Could not load users")));
  }, []);
  const onSaved = (u) => {
    setUsers((list) => upsert(list, u));
    if (u.id === user.id) setUser(u);
  };

  return (
    <div className="mx-auto max-w-4xl space-y-5" data-testid="users-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Users</h1>
        <p className="text-sm text-slate-400">Admins add accounts here. There is no public sign-up.</p>
      </div>
      <AddUserForm reps={reps} onAdded={(u) => setUsers((list) => upsert(list, u))} />
      {error && <p className="text-sm text-amber-400">{error}</p>}
      <p data-testid="users-count" className="font-mono text-xs text-slate-400">{users.length} users</p>
      <ul data-testid="users-list" className="divide-y divide-slate-800 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
        {users.map((u) => (
          <UserRow key={`${u.id}-${u.name}-${u.role}-${u.rep_id}-${u.active}`} u={u} reps={reps} rep={repsById[u.rep_id]}
            isMe={u.id === user.id} onSaved={onSaved} />
        ))}
      </ul>
    </div>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  return user.role === "Admin" ? <UsersAdmin /> : <NoAccess />;
}
